// The admin Courses page: a page-backed MeTable over the rows endpoint. Each loaded page then asks the AI
// endpoint for its courses' AI-graded state, in chunks, without holding up the rows. The page keeps the
// table's published state for the tab (sessionStorage).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { MeTable } from '@CommonComponents'
import { client } from '@Utils'
import { rowActions, tableConfig } from '../config'
import { AiFeaturesContext, CopyLiveRegion } from '../cells'
import { readState, writeState } from '../sessionState'

export const AI_CHUNK = 50

const actionsFor = row => rowActions(row)

const chunks = (items, size) => {
  const out = []
  for (let i = 0; i < items.length; i += size)
    out.push(items.slice(i, i + size))
  return out
}

const newController = () =>
  typeof AbortController === 'undefined' ? null : new AbortController()

const openDialog = options => {
  if (options && window.$ && window.$.meDialog) window.$.meDialog.open2(options)
}

export const AdminCoursesTable = ({
  rowsUrl,
  aiUrl,
  user,
  canCreate = false,
  canReset = false,
  newCourseDialog = null,
  resetDialog = null,
}) => {
  const [restored] = useState(() => readState(user))
  const [controlledState, setControlledState] = useState(restored)
  const [deletedExist, setDeletedExist] = useState(false)
  const [aiFeatures, setAiFeatures] = useState({})
  const published = useRef(restored)
  const aiController = useRef(null)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      if (aiController.current) aiController.current.abort()
    }
  }, [])

  // a page's AI states arrive after its rows; an answer that is not a boolean is unavailable (null)
  const loadAi = useCallback(
    (coursenums, signal) => {
      chunks(coursenums, AI_CHUNK).forEach(chunk => {
        client
          .post(aiUrl, { coursenums: chunk }, { signal })
          .then(
            response => {
              const features = (response.data && response.data.features) || {}
              return chunk.reduce((answered, c) => {
                const value = features[c]
                answered[c] = typeof value === 'boolean' ? value : null
                return answered
              }, {})
            },
            () =>
              chunk.reduce((failed, c) => {
                failed[c] = null
                return failed
              }, {})
          )
          .then(answers => {
            if (!mounted.current || (signal && signal.aborted)) return
            setAiFeatures(prev => ({ ...prev, ...answers }))
          })
      })
    },
    [aiUrl]
  )

  const load = useCallback(
    (request, { signal } = {}) => {
      if (aiController.current) aiController.current.abort()
      aiController.current = null
      return client
        .post(rowsUrl, request, signal ? { signal } : undefined)
        .then(response => {
          const data = response.data || {}
          const rows = Array.isArray(data.rows) ? data.rows : []
          // a superseded page starts nothing; the table discards its rows
          if (!mounted.current || (signal && signal.aborted))
            return { rows, total: data.total }
          setDeletedExist(Boolean(data.deletedExist))
          const coursenums = Array.from(
            new Set(rows.map(row => row.coursenum).filter(Boolean))
          )
          if (coursenums.length) {
            const ai = newController()
            aiController.current = ai
            // known answers stay; the rest show as pending until their chunk answers
            setAiFeatures(prev => {
              const next = { ...prev }
              coursenums.forEach(c => {
                if (typeof next[c] !== 'boolean') delete next[c]
              })
              return next
            })
            loadAi(coursenums, ai ? ai.signal : undefined)
          }
          return { rows, total: data.total }
        })
    },
    [rowsUrl, loadAi]
  )

  const onStateChange = useCallback(
    (state, event) => {
      if (event && event.change === 'expansion') return
      published.current = state
      writeState(user, state)
    },
    [user]
  )

  // the table merges controlled state only when it changes, so each request carries a fresh nonce
  const nonce = useRef(0)
  const onShowDeleted = useCallback(() => {
    nonce.current += 1
    const filters = { status: ['Deleted'] }
    setControlledState({ filters, page: 1, nonce: nonce.current })
    // a controlled merge publishes nothing, so record it here
    const state = { ...(published.current || {}), filters, page: 1 }
    published.current = state
    writeState(user, state)
  }, [user])

  const onNewCourse = useCallback(
    () => openDialog(newCourseDialog),
    [newCourseDialog]
  )
  const onResetDemo = useCallback(() => openDialog(resetDialog), [resetDialog])

  const config = useMemo(
    () =>
      tableConfig({
        canCreate,
        canReset,
        deletedExist,
        onShowDeleted,
        onNewCourse,
        onResetDemo,
      }),
    [canCreate, canReset, deletedExist, onShowDeleted, onNewCourse, onResetDemo]
  )

  return (
    <AiFeaturesContext.Provider value={aiFeatures}>
      <MeTable
        config={config}
        load={load}
        controlledState={controlledState}
        rowActions={actionsFor}
        onStateChange={onStateChange}
      />
      <CopyLiveRegion />
    </AiFeaturesContext.Provider>
  )
}

AdminCoursesTable.propTypes = {
  rowsUrl: PropTypes.string.isRequired,
  aiUrl: PropTypes.string.isRequired,
  user: PropTypes.string.isRequired,
  canCreate: PropTypes.bool,
  canReset: PropTypes.bool,
  newCourseDialog: PropTypes.object,
  resetDialog: PropTypes.object,
}
